import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import { LoadingState } from '../../../components/AsyncStates';
import { useWorkPointRules } from '@/hooks/useWorkPointRules';
import { useReferees } from '@/hooks/useReferees';
import { createRule, updateRule, estimatePoints } from '@/api/TournamentManagement/workPointApi';
import type {
  CreateWorkPointRuleInput,
  UpdateWorkPointRuleInput,
  WorkPointEstimateQuery,
  WorkPointEstimateView,
} from '@/models/tournament';
import { MatchRole, MatchFormat, MatchType } from '@/models/tournament';
import { useCurrency } from '@/hooks/useCurrency';

const defaultFormValues: CreateWorkPointRuleInput = {
  headRefereeBasePoints: 100,
  assistantRefereeBasePoints: 60,
  fourthOfficialBasePoints: 30,
  fiveAsidePoints: 20,
  sevenAsidePoints: 40,
  nineAsidePoints: 60,
  elevenAsidePoints: 80,
  indoorBonus: 10,
  outdoorBonus: 0,
  weekendHolidayBonus: 15,
  nightMatchBonus: 10,
  nightMatchThresholdHour: 20,
  earlyMorningBonus: 5,
  earlyMorningThresholdHour: 8,
  highRatingThreshold: 4.7,
  highRatingBonus: 15,
  midRatingThreshold: 4.0,
  midRatingBonus: 5,
  minPointsPerMatch: 10,
  maxPointsPerMatch: 300,
  pointToCurrencyRate: 1.0,
  effectiveFrom: new Date().toISOString().split('T')[0],
  effectiveTo: undefined,
};

const inputClasses =
  'w-full px-3 py-2 text-sm bg-background border border-input rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors outline-none';

interface NumberFieldProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  testId: string;
  step?: string;
  min?: number;
}

const NumberField: React.FC<NumberFieldProps> = ({ label, value, onChange, testId, step = '1', min = 0 }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className={inputClasses}
      step={step}
      min={min}
      data-testid={testId}
    />
  </div>
);

const WorkPointSettings: React.FC = () => {
  const { t } = useTranslation('tournament');
  const { code: currencyCode, format: formatCurrency } = useCurrency();
  const { data: rules, loading, error, refetch } = useWorkPointRules();
  const [form, setForm] = useState<CreateWorkPointRuleInput>(defaultFormValues);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Preview calculator state
  const [previewRole, setPreviewRole] = useState<MatchRole | ''>('');
  const [previewFormat, setPreviewFormat] = useState<MatchFormat | ''>('');
  const [previewType, setPreviewType] = useState<MatchType | ''>('');
  // Explicit date+time so the server can determine weekend/holiday, night, early-morning
  const nowForDefault = new Date();
  const [previewDate, setPreviewDate] = useState<string>(nowForDefault.toISOString().split('T')[0] ?? '');
  const [previewHour, setPreviewHour] = useState<string>('14:00');
  const [previewRefereeProfileId, setPreviewRefereeProfileId] = useState<string>('');
  const [previewResult, setPreviewResult] = useState<WorkPointEstimateView | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Referees list for the "assume this referee" dropdown — so performance bonus shows up
  const { data: refereesList } = useReferees({ pageSize: 100 });

  const activeRule = useMemo(() => {
    if (!rules || rules.length === 0) return null;
    return rules.find((r) => r.isActive) ?? rules[0];
  }, [rules]);

  useEffect(() => {
    if (activeRule) {
      setForm({
        headRefereeBasePoints: activeRule.headRefereeBasePoints,
        assistantRefereeBasePoints: activeRule.assistantRefereeBasePoints,
        fourthOfficialBasePoints: activeRule.fourthOfficialBasePoints,
        fiveAsidePoints: activeRule.fiveAsidePoints,
        sevenAsidePoints: activeRule.sevenAsidePoints,
        nineAsidePoints: activeRule.nineAsidePoints,
        elevenAsidePoints: activeRule.elevenAsidePoints,
        indoorBonus: activeRule.indoorBonus,
        outdoorBonus: activeRule.outdoorBonus,
        weekendHolidayBonus: activeRule.weekendHolidayBonus,
        nightMatchBonus: activeRule.nightMatchBonus,
        nightMatchThresholdHour: activeRule.nightMatchThresholdHour,
        earlyMorningBonus: activeRule.earlyMorningBonus,
        earlyMorningThresholdHour: activeRule.earlyMorningThresholdHour,
        highRatingThreshold: activeRule.highRatingThreshold,
        highRatingBonus: activeRule.highRatingBonus,
        midRatingThreshold: activeRule.midRatingThreshold,
        midRatingBonus: activeRule.midRatingBonus,
        minPointsPerMatch: activeRule.minPointsPerMatch,
        maxPointsPerMatch: activeRule.maxPointsPerMatch,
        pointToCurrencyRate: activeRule.pointToCurrencyRate,
        effectiveFrom: activeRule.effectiveFrom?.split('T')[0] ?? '',
        effectiveTo: activeRule.effectiveTo?.split('T')[0],
      });
    }
  }, [activeRule]);

  const updateField = <K extends keyof CreateWorkPointRuleInput>(key: K, value: CreateWorkPointRuleInput[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      let result;
      if (activeRule) {
        const input: UpdateWorkPointRuleInput = { ...form, id: activeRule.id };
        result = await updateRule(input);
      } else {
        result = await createRule(form);
      }
      if (result.status < 400 && result.errors.length === 0) {
        setSaveSuccess(true);
        refetch();
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setSaveError(result.errors[0]?.description || t('Failed to save rule'));
      }
    } catch (error: any) {
      const msg = error?.response?.data?.errors?.[0]?.description
        || error?.message
        || t('Something went wrong. Please try again.');
      setSaveError(msg);
      console.error('Error saving work point rule:', error);
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = useCallback(async () => {
    if (previewRole === '' || previewFormat === '' || previewType === '' || !previewDate || !previewHour) {
      setPreviewResult(null);
      return;
    }
    setPreviewLoading(true);
    try {
      // Send as local-wall-clock (no UTC shift). Night/early-morning thresholds are
      // compared against the HOUR the user picked locally — converting to UTC via
      // toISOString() would send a different hour and break the bonus calculation
      // for any viewer outside UTC (e.g. 20:00 local in UTC+3 becomes 17:00Z and
      // falls below the night threshold).
      const [h, m] = previewHour.split(':').map((s) => parseInt(s, 10));
      const hh = String(isNaN(h) ? 14 : h).padStart(2, '0');
      const mm = String(isNaN(m) ? 0 : m).padStart(2, '0');
      const localIso = `${previewDate}T${hh}:${mm}:00`;

      const query: WorkPointEstimateQuery = {
        role: previewRole as MatchRole,
        format: previewFormat as MatchFormat,
        type: previewType as MatchType,
        scheduledDateTime: localIso,
        refereeProfileId: previewRefereeProfileId ? parseInt(previewRefereeProfileId, 10) : undefined,
      };
      const result = await estimatePoints(query);
      if (result.status < 400 && result.errors.length === 0) {
        setPreviewResult(result.payload as WorkPointEstimateView);
      }
    } catch {
      // silently fail
    } finally {
      setPreviewLoading(false);
    }
  }, [previewRole, previewFormat, previewType, previewDate, previewHour, previewRefereeProfileId]);

  useEffect(() => {
    if (previewRole !== '' && previewFormat !== '' && previewType !== '' && previewDate && previewHour) {
      handlePreview();
    }
  }, [handlePreview, previewRole, previewFormat, previewType, previewDate, previewHour, previewRefereeProfileId]);

  if (loading) {
    return (
      <div className="p-6">
        <LoadingState title={t('Loading data...')} />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="work-point-settings-page">
      {/* Page Header */}
      <div className="flex items-center space-x-3 rtl:space-x-reverse">
        <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-lg flex items-center justify-center shadow-sm">
          <Icon name="Calculator" size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('Work Point Settings')}</h1>
          <p className="text-sm text-gray-600">{t('Configure work point calculation rules for referee compensation')}</p>
        </div>
      </div>

      {/* Active Rule Status */}
      <div className="bg-card border border-border rounded-lg p-4" data-testid="active-rule-status">
        <div className="flex items-center space-x-2 rtl:space-x-reverse mb-2">
          <Icon name={activeRule ? 'CheckCircle' : 'AlertCircle'} size={18} className={activeRule ? 'text-green-600' : 'text-amber-600'} />
          <h2 className="text-lg font-semibold text-gray-900">{t('Active Rule')}</h2>
        </div>
        {activeRule ? (
          <p className="text-sm text-gray-600">
            ID: {activeRule.id} | {t('Effective From')}: {new Date(activeRule.effectiveFrom).toLocaleDateString()}
            {activeRule.effectiveTo && ` | ${t('Effective To')}: ${new Date(activeRule.effectiveTo).toLocaleDateString()}`}
          </p>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
            <p className="text-sm text-amber-800">{t('No work point calculation rule has been configured yet. Create one below.')}</p>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Form Sections */}
      <div className="space-y-6">
        {/* Base Points by Role */}
        <section className="bg-card border border-border rounded-lg p-6" data-testid="section-base-points">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Icon name="Users" size={18} className="me-2 text-primary" />
            {t('Base Points by Role')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <NumberField
              label={t('Head Referee Base')}
              value={form.headRefereeBasePoints}
              onChange={(v) => updateField('headRefereeBasePoints', v)}
              testId="input-head-referee-base"
            />
            <NumberField
              label={t('Assistant Referee Base')}
              value={form.assistantRefereeBasePoints}
              onChange={(v) => updateField('assistantRefereeBasePoints', v)}
              testId="input-assistant-referee-base"
            />
            <NumberField
              label={t('Fourth Official Base')}
              value={form.fourthOfficialBasePoints}
              onChange={(v) => updateField('fourthOfficialBasePoints', v)}
              testId="input-fourth-official-base"
            />
          </div>
        </section>

        {/* Points by Match Format */}
        <section className="bg-card border border-border rounded-lg p-6" data-testid="section-format-points">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Icon name="LayoutGrid" size={18} className="me-2 text-primary" />
            {t('Points by Match Format')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <NumberField
              label={t('5-aside Points')}
              value={form.fiveAsidePoints}
              onChange={(v) => updateField('fiveAsidePoints', v)}
              testId="input-five-aside"
            />
            <NumberField
              label={t('7-aside Points')}
              value={form.sevenAsidePoints}
              onChange={(v) => updateField('sevenAsidePoints', v)}
              testId="input-seven-aside"
            />
            <NumberField
              label={t('9-aside Points')}
              value={form.nineAsidePoints}
              onChange={(v) => updateField('nineAsidePoints', v)}
              testId="input-nine-aside"
            />
            <NumberField
              label={t('11-aside Points')}
              value={form.elevenAsidePoints}
              onChange={(v) => updateField('elevenAsidePoints', v)}
              testId="input-eleven-aside"
            />
          </div>
        </section>

        {/* Match Type Bonuses */}
        <section className="bg-card border border-border rounded-lg p-6" data-testid="section-type-bonuses">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Icon name="MapPin" size={18} className="me-2 text-primary" />
            {t('Match Type Bonuses')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <NumberField
              label={t('Indoor Bonus')}
              value={form.indoorBonus}
              onChange={(v) => updateField('indoorBonus', v)}
              testId="input-indoor-bonus"
            />
            <NumberField
              label={t('Outdoor Bonus')}
              value={form.outdoorBonus}
              onChange={(v) => updateField('outdoorBonus', v)}
              testId="input-outdoor-bonus"
            />
          </div>
        </section>

        {/* Time-Based Bonuses */}
        <section className="bg-card border border-border rounded-lg p-6" data-testid="section-time-bonuses">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Icon name="Clock" size={18} className="me-2 text-primary" />
            {t('Time-Based Bonuses')}
          </h3>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3 mb-4 text-xs text-blue-900 dark:text-blue-100" data-testid="time-bonus-help">
            <p className="font-medium mb-1 flex items-center"><Icon name="Info" size={13} className="me-1.5" />{t('How time-based bonuses are applied')}</p>
            <ul className="list-disc list-inside space-y-1 ms-1">
              <li>{t('Weekend/Holiday — applied when the match falls on a company holiday or a day with no scheduled shifts')}</li>
              <li>{t('Night match — applied when the match starts at or after the Night Threshold Hour (e.g. threshold of 20 means 8 PM and later)')}</li>
              <li>{t('Early morning — applied when the match starts strictly before the Early Morning Threshold Hour (e.g. threshold of 8 means any time before 8 AM)')}</li>
              <li>{t('All three can stack. Hours are interpreted as the local wall-clock time the match was scheduled for (not UTC)')}</li>
            </ul>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <NumberField
              label={t('Weekend/Holiday Bonus')}
              value={form.weekendHolidayBonus}
              onChange={(v) => updateField('weekendHolidayBonus', v)}
              testId="input-weekend-holiday-bonus"
            />
            <NumberField
              label={t('Night Match Bonus')}
              value={form.nightMatchBonus}
              onChange={(v) => updateField('nightMatchBonus', v)}
              testId="input-night-match-bonus"
            />
            <NumberField
              label={t('Night Match Threshold Hour')}
              value={form.nightMatchThresholdHour}
              onChange={(v) => updateField('nightMatchThresholdHour', v)}
              testId="input-night-threshold"
              min={0}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <NumberField
              label={t('Early Morning Bonus')}
              value={form.earlyMorningBonus}
              onChange={(v) => updateField('earlyMorningBonus', v)}
              testId="input-early-morning-bonus"
            />
            <NumberField
              label={t('Early Morning Threshold Hour')}
              value={form.earlyMorningThresholdHour}
              onChange={(v) => updateField('earlyMorningThresholdHour', v)}
              testId="input-early-morning-threshold"
              min={0}
            />
          </div>
        </section>

        {/* Performance Bonuses */}
        <section className="bg-card border border-border rounded-lg p-6" data-testid="section-performance-bonuses">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Icon name="TrendingUp" size={18} className="me-2 text-primary" />
            {t('Performance Bonuses')}
          </h3>
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-md p-3 mb-4 text-xs text-purple-900 dark:text-purple-100" data-testid="performance-bonus-help">
            <p className="font-medium mb-1 flex items-center"><Icon name="Info" size={13} className="me-1.5" />{t('How performance bonuses are applied')}</p>
            <ul className="list-disc list-inside space-y-1 ms-1">
              <li>{t('Each referee has a rolling performance rating (0–5) maintained on their profile, updated whenever HR rates them after a completed match.')}</li>
              <li>{t('When the work points are calculated, only ONE tier applies — the highest one the rating qualifies for')}</li>
              <li className="ms-4">{t('• If rating ≥ High Rating Threshold → add High Rating Bonus.')}</li>
              <li className="ms-4">{t('• Else if rating ≥ Mid Rating Threshold → add Mid Rating Bonus.')}</li>
              <li className="ms-4">{t('• Otherwise → no performance bonus.')}</li>
              <li>{t('The bonuses are additive with the base, format, type, and time bonuses, then the total is clamped between the Min and Max point caps.')}</li>
            </ul>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <NumberField
              label={t('High Rating Threshold')}
              value={form.highRatingThreshold}
              onChange={(v) => updateField('highRatingThreshold', v)}
              testId="input-high-rating-threshold"
              step="0.1"
            />
            <NumberField
              label={t('High Rating Bonus')}
              value={form.highRatingBonus}
              onChange={(v) => updateField('highRatingBonus', v)}
              testId="input-high-rating-bonus"
            />
            <NumberField
              label={t('Mid Rating Threshold')}
              value={form.midRatingThreshold}
              onChange={(v) => updateField('midRatingThreshold', v)}
              testId="input-mid-rating-threshold"
              step="0.1"
            />
            <NumberField
              label={t('Mid Rating Bonus')}
              value={form.midRatingBonus}
              onChange={(v) => updateField('midRatingBonus', v)}
              testId="input-mid-rating-bonus"
            />
          </div>
        </section>

        {/* Point Caps */}
        <section className="bg-card border border-border rounded-lg p-6" data-testid="section-point-caps">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Icon name="Shield" size={18} className="me-2 text-primary" />
            {t('Point Caps')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <NumberField
              label={t('Min Points Per Match')}
              value={form.minPointsPerMatch}
              onChange={(v) => updateField('minPointsPerMatch', v)}
              testId="input-min-points"
            />
            <NumberField
              label={t('Max Points Per Match')}
              value={form.maxPointsPerMatch}
              onChange={(v) => updateField('maxPointsPerMatch', v)}
              testId="input-max-points"
            />
          </div>
        </section>

        {/* Currency Rate */}
        <section className="bg-card border border-border rounded-lg p-6" data-testid="section-currency-rate">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Icon name="DollarSign" size={18} className="me-2 text-primary" />
            {t('Currency Rate')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <NumberField
              label={`${t('Point to Currency Rate')}${currencyCode ? ` (${currencyCode})` : ''}`}
              value={form.pointToCurrencyRate}
              onChange={(v) => updateField('pointToCurrencyRate', v)}
              testId="input-currency-rate"
              step="0.01"
            />
          </div>
        </section>

        {/* Effective Date */}
        <section className="bg-card border border-border rounded-lg p-6" data-testid="section-effective-date">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Icon name="Calendar" size={18} className="me-2 text-primary" />
            {t('Effective Date')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('Effective From')}</label>
              <input
                type="date"
                value={form.effectiveFrom}
                onChange={(e) => updateField('effectiveFrom', e.target.value)}
                className={inputClasses}
                data-testid="input-effective-from"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('Effective To')} <span className="text-gray-400 text-xs">({t('Optional')})</span>
              </label>
              <input
                type="date"
                value={form.effectiveTo ?? ''}
                onChange={(e) => updateField('effectiveTo', e.target.value || undefined)}
                className={inputClasses}
                data-testid="input-effective-to"
              />
            </div>
          </div>
        </section>

        {/* Preview Calculator */}
        <section className="bg-card border border-border rounded-lg p-6" data-testid="section-preview-calculator">
          <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center">
            <Icon name="Zap" size={18} className="me-2 text-primary" />
            {t('Preview Calculator')}
          </h3>
          <p className="text-xs text-gray-500 mb-4">{t('Estimates a referee match compensation using the active rule — all parameters on this page are reflected below (base, format, type, time bonuses, performance, caps, and currency).')}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('Role')}</label>
              <select
                value={previewRole}
                onChange={(e) => setPreviewRole(e.target.value === '' ? '' : (Number(e.target.value) as MatchRole))}
                className={inputClasses}
                data-testid="preview-role"
              >
                <option value="">{t('Select a role')}</option>
                <option value={MatchRole.HeadReferee}>{t('Head Referee')}</option>
                <option value={MatchRole.AssistantReferee}>{t('Assistant Referee')}</option>
                <option value={MatchRole.FourthOfficial}>{t('Fourth Official')}</option>
                <option value={MatchRole.Commissioner}>{t('Commissioner')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('Match Format')}</label>
              <select
                value={previewFormat}
                onChange={(e) => setPreviewFormat(e.target.value === '' ? '' : (Number(e.target.value) as MatchFormat))}
                className={inputClasses}
                data-testid="preview-format"
              >
                <option value="">{t('Select a format')}</option>
                <option value={MatchFormat.FiveAside}>{t('5 aside')}</option>
                <option value={MatchFormat.SevenAside}>{t('7 aside')}</option>
                <option value={MatchFormat.NineAside}>{t('9 aside')}</option>
                <option value={MatchFormat.ElevenAside}>{t('11 aside')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('Match Type')}</label>
              <select
                value={previewType}
                onChange={(e) => setPreviewType(e.target.value === '' ? '' : (Number(e.target.value) as MatchType))}
                className={inputClasses}
                data-testid="preview-type"
              >
                <option value="">{t('Select a type')}</option>
                <option value={MatchType.Indoor}>{t('Indoor')}</option>
                <option value={MatchType.Outdoor}>{t('Outdoor')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('Date')}</label>
              <input
                type="date"
                value={previewDate}
                onChange={(e) => setPreviewDate(e.target.value)}
                className={inputClasses}
                data-testid="preview-date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('Time')}</label>
              <input
                type="time"
                value={previewHour}
                onChange={(e) => setPreviewHour(e.target.value)}
                className={inputClasses}
                data-testid="preview-hour"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('Referee')} <span className="text-gray-400 text-xs">({t('Optional')})</span></label>
              <select
                value={previewRefereeProfileId}
                onChange={(e) => setPreviewRefereeProfileId(e.target.value)}
                className={inputClasses}
                data-testid="preview-referee"
              >
                <option value="">{t('No specific referee')}</option>
                {(refereesList?.items ?? []).map((r: any) => (
                  <option key={r.id} value={String(r.id)}>{r.name}{typeof r.rating === 'number' ? ` · ${r.rating.toFixed(1)}★` : ''}</option>
                ))}
              </select>
            </div>
          </div>
          {previewLoading && (
            <div className="py-4">
              <LoadingState size="sm" title={t('Loading data...')} />
            </div>
          )}
          {previewResult && !previewLoading && (
            <div className="space-y-3" data-testid="preview-result">
              <div className="bg-gradient-to-r from-primary/10 to-blue-50 rounded-lg p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('Estimated Points')}</p>
                  <p className="text-3xl font-bold text-primary">{previewResult.estimatedPoints}</p>
                </div>
                <div className="text-end">
                  <p className="text-sm text-gray-600">{t('Currency Equivalent')}</p>
                  <p className="text-2xl font-bold text-green-700">{formatCurrency(previewResult.estimatedCurrencyAmount)}</p>
                </div>
              </div>
              <div className="bg-muted/40 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-900 mb-2">{t('Full Breakdown')}</p>
                <div className="flex flex-wrap gap-2 text-sm">
                  <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded">{t('Base')}: {previewResult.baseRolePoints}</span>
                  <span className="text-gray-400">+</span>
                  <span className="bg-indigo-50 text-indigo-700 px-2 py-1 rounded">{t('Format')}: {previewResult.formatPoints}</span>
                  <span className="text-gray-400">+</span>
                  <span className="bg-emerald-50 text-emerald-700 px-2 py-1 rounded">{t('Type')}: {previewResult.typeBonus}</span>
                  <span className="text-gray-400">+</span>
                  <span className="bg-amber-50 text-amber-700 px-2 py-1 rounded">{t('Time')}: {previewResult.timeBonus}</span>
                  <span className="text-gray-400">+</span>
                  <span className="bg-pink-50 text-pink-700 px-2 py-1 rounded">{t('Performance')}: {previewResult.performanceBonus}</span>
                  <span className="text-gray-400">=</span>
                  <span className="bg-slate-100 text-slate-800 px-2 py-1 rounded">{t('Subtotal')}: {previewResult.rawSubtotal}</span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-600">
                  <span>
                    <Icon name="Shield" size={12} className="inline me-1 text-gray-400" />
                    {t('Caps')}: {previewResult.minPointsPerMatch}–{previewResult.maxPointsPerMatch}
                  </span>
                  <span>
                    <Icon name="DollarSign" size={12} className="inline me-1 text-gray-400" />
                    {t('Rate')}: {previewResult.pointToCurrencyRate}{currencyCode ? `/${currencyCode}` : ''}
                  </span>
                  <span>
                    <Icon name={previewResult.isNonWorkingDay ? 'Calendar' : 'Sun'} size={12} className="inline me-1 text-gray-400" />
                    {previewResult.isNonWorkingDay ? t('Weekend / holiday') : t('Working day')}
                  </span>
                  {previewResult.rawSubtotal !== previewResult.estimatedPoints && (
                    <span className="text-amber-700">
                      <Icon name="AlertTriangle" size={12} className="inline me-1" />
                      {t('Adjusted by caps')}: {previewResult.rawSubtotal} → {previewResult.estimatedPoints}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Save Button + Status */}
      <div className="flex items-center space-x-4 rtl:space-x-reverse pt-4 pb-8">
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-primary/20"
          data-testid="save-rule-btn"
        >
          {saving ? (
            <>
              <Icon name="Loader2" size={18} className="me-2 animate-spin" />
              {t('Saving...')}
            </>
          ) : (
            <>
              <Icon name="Save" size={18} className="me-2" />
              {activeRule ? t('Update Rule') : t('Create Rule')}
            </>
          )}
        </button>
        {saveSuccess && (
          <span className="inline-flex items-center text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg border border-green-200">
            <Icon name="CheckCircle" size={16} className="me-2" />
            {t('Rule saved successfully')}
          </span>
        )}
        {saveError && (
          <span className="inline-flex items-center text-sm text-red-700 bg-red-50 px-3 py-2 rounded-lg border border-red-200">
            <Icon name="AlertCircle" size={16} className="me-2" />
            {saveError}
          </span>
        )}
      </div>
    </div>
  );
};

export default WorkPointSettings;
